import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../theme";
import {
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  LIVE_PRICES,
  PRICING_GHLEvent,
} from "./pricingData";

interface IPlan {
  planName: string;
  monthly: number;
  annualPlanPerMonth: number;
  annualMaxPrice: number;
  savingsAmount?: number;
  addons?: string[];
  liveAttendees?: number;
  automatedAttendees?: number;
  attendees?: number;
  MonthlyPlanID?: string;
  YearlyPlanID?: string;
}

interface PricingSectionProps {
  currentPlanId?: string;
  currentAttendees?: number;
  onPlanSelect: (planId: string) => void;
  hideTabs?: boolean;
  id?: string;
  subscriptionStatus?: string;
}

const planFeaturesList = (isYearly?: boolean) => ({
  "50": [
    {
      title: "What's Included",
      list: [
        {
          label: "AI Webinar Builder",
          tooltip:
            "Create webinar pages, flows, and presentations instantly using AI.",
        },
        { label: "Create up to 2 AI webinar funnels", tooltip: "" },
        { label: "Live webinars", tooltip: "" },
        { label: "Up to 5 live webinars", tooltip: "" },
        { label: "Standard support", tooltip: "" },
      ],
    },
  ],
  "200": [
    {
      title: "What's Included",
      list: [
        { label: "AI Webinar Builder", tooltip: "" },
        { label: "Create up to 5 AI webinar funnels", tooltip: "" },
        { label: "Live webinars", tooltip: "" },
        { label: "Automated / Simulive", tooltip: "" },
        { label: "Evergreen", tooltip: "" },
        { label: "Unlimited webinars", tooltip: "" },
        { label: "Stream to LinkedIn, FB, YouTube /RTMP", tooltip: "" },
        {
          label: "Paid webinars",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        {
          label: "Built-in Sales CRM (EasyCRM)",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        { label: "Priority support + Manager", tooltip: "" },
      ],
    },
  ],
  "500": [
    {
      title: "What's Included",
      list: [
        { label: "AI Webinar Builder", tooltip: "" },
        { label: "Create up to 10 AI webinar funnels", tooltip: "" },
        { label: "Live webinars", tooltip: "" },
        { label: "Automated / Simulive", tooltip: "" },
        { label: "Evergreen", tooltip: "" },
        { label: "Unlimited webinars", tooltip: "" },
        { label: "Stream to LinkedIn, FB, YouTube /RTMP", tooltip: "" },
        {
          label: "Paid webinars",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        {
          label: "Built-in Sales CRM (EasyCRM)",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        { label: "Priority support + Manager", tooltip: "" },
      ],
    },
  ],
  "3000": [
    {
      title: "What's Included",
      list: [
        { label: "3 Active Evergreen Funnels", tooltip: "" },
        { label: "Create up to 2 AI webinar funnels", tooltip: "" },
        {
          label: "Paid webinars",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        {
          label: "Built-in Sales CRM (EasyCRM)",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        { label: "Standard support", tooltip: "" },
      ],
    },
  ],
  "6000": [
    {
      title: "What's Included",
      list: [
        { label: "6 Active Evergreen Funnels", tooltip: "" },
        { label: "Create up to 10 AI webinar funnels", tooltip: "" },
        {
          label: "Paid webinars",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        {
          label: "Built-in Sales CRM (EasyCRM)",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        { label: "Priority support", tooltip: "" },
      ],
    },
  ],
  "10000": [
    {
      title: "What's Included",
      list: [
        { label: "10 Active Evergreen Funnels", tooltip: "" },
        { label: "Create up to 20 AI webinar funnels", tooltip: "" },
        {
          label: "Paid webinars",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        {
          label: "Built-in Sales CRM (EasyCRM)",
          tooltip: "",
          tag: isYearly ? "+$49/mo add-on" : "+$49/mo add-on",
        },
        { label: "Priority support + Manager", tooltip: "" },
      ],
    },
  ],
});

const PricingSectionV2GHL: React.FC<PricingSectionProps> = ({
  currentPlanId,
  currentAttendees,
  onPlanSelect,
  hideTabs = false,
  id,
  subscriptionStatus,
}) => {
  const [planDuration, setPlanDuration] = useState<"monthly" | "yearly">(
    "yearly",
  );
  const [tabValue, setTabValue] = useState(0);
  const [liveValue, setLiveValue] = useState("50");
  const [automatedValue, setAutomatedValue] = useState("3000");
  const [liveAutomatedValue, setLiveAutomatedValue] = useState("200");
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [categoryTab, setCategoryTab] = useState<0 | 1>(0);

  useEffect(() => {
    if (!currentPlanId || !currentAttendees) return;
    if (currentPlanId.includes("Yearly")) setPlanDuration("yearly");
    if (currentPlanId.includes("Monthly")) setPlanDuration("monthly");
    if (currentPlanId.includes("Live-Webinars")) {
      const plan = LIVE_PRICES.find((p) => p.value === currentAttendees.toString());
      if (plan) setLiveValue(plan.value);
    } else if (currentPlanId.includes("Automated-Gold")) {
      const plan = AUTOMATED_PRICES.find(
        (p) => p.value === currentAttendees.toString(),
      );
      if (plan) setAutomatedValue(plan.value);
    } else if (currentPlanId.includes("All-In-One")) {
      const plan = LIVE_AUTO_PRICES.find(
        (p) => p.value === currentAttendees.toString(),
      );
      if (plan) setLiveAutomatedValue(plan.value);
    }
  }, [currentPlanId, currentAttendees]);

  const eventPlanKeys = ["50", "200", "500"] as const;
  const funnelPlanKeys = ["3000", "6000", "10000"] as const;

  const planCardsData = useMemo(
    () =>
      categoryTab === 0
        ? eventPlanKeys.map((k) => ({ key: k, plan: PRICING_GHLEvent.events[k] }))
        : funnelPlanKeys.map((k) => ({ key: k, plan: PRICING_GHLEvent.funnels[k] })),
    [categoryTab],
  );

  const getDisplayPrice = (
    plan: { monthly: number; annualPlanPerMonth: number },
    durationOverride?: "monthly" | "yearly",
  ) => {
    const dur = durationOverride ?? planDuration;
    const base = dur === "yearly" ? plan.annualPlanPerMonth : plan.monthly;
    return (base * 0.35).toFixed(2);
  };

  const getPlanDescription = (planKey: string) => {
    if (categoryTab === 0) {
      const d: Record<string, string> = {
        "50": "Best for small live webinars",
        "200": "Best for live, simulive & evergreen webinars",
        "500": "Best for high-volume live & automated webinars",
      };
      return d[planKey] ?? "";
    }
    const d: Record<string, string> = {
      "3000": "Best for getting started with evergreen",
      "6000": "Best for scaling automated webinars",
      "10000": "Best for high-volume evergreen",
    };
    return d[planKey] ?? "";
  };

  const selectPlan = (plan: IPlan, isYearly: boolean) => {
    const planId = isYearly ? plan.YearlyPlanID : plan.MonthlyPlanID;
    if (planId) onPlanSelect(planId);
  };

  const PlanCard = ({
    planKey,
    plan,
    isMostPopular,
  }: {
    planKey: string;
    plan: IPlan;
    isMostPopular?: boolean;
  }) => {
    const isYearly = planDuration === "yearly";
    const selectedPlanId = isYearly ? plan.YearlyPlanID : plan.MonthlyPlanID;
    const isCurrentCardPlan =
      !!currentPlanId &&
      !!selectedPlanId &&
      `${currentPlanId}-${currentAttendees}` === selectedPlanId;

    return (
      <Grid
        key={planKey}
        size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }}
        maxWidth={{ xs: "350px", md: "unset" }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: "12px",
            minHeight: 420,
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: isMostPopular
              ? (theme.palette.primary?.main ?? "#178EFC")
              : "#E8EAF0",
            background: "#FFF",
            boxShadow: isMostPopular
              ? "0px 4px 20px rgba(23, 142, 252, 0.15)"
              : "0px 4px 12px 0px rgba(43, 43, 48, 0.06)",
            position: "relative",
            mt: isMostPopular ? "-35px" : 0,
            height: isMostPopular ? "calc(100% + 35px)" : "100%",
          }}
        >
          {isMostPopular && (
            <Box
              sx={{
                background: theme.palette.primary?.main ?? "#178EFC",
                color: "#fff",
                py: 1,
                textAlign: "center",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              Most Popular
            </Box>
          )}
          <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            {isCurrentCardPlan && subscriptionStatus === "in_trial" && (
              <Box
                sx={{
                  position: "absolute",
                  top: isMostPopular ? 40 : 0,
                  right: 0,
                  background: "#22B060",
                  color: "#fff",
                  p: "4px 8px 4px 12px",
                  borderRadius: "0 8px 0 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                Currently in trial
              </Box>
            )}
            <Typography variant="h6" fontWeight={600} mb={0.25} align="left">
              {plan.planName}
            </Typography>
            <Typography
              variant="body2"
              color="#666"
              align="left"
              minHeight={50}
              sx={{ fontSize: "13px" }}
            >
              {getPlanDescription(planKey)}
            </Typography>
            {categoryTab !== 1 && (
              <Typography
                variant="body2"
                color="#000"
                sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}
              >
                {planKey === "50" && "50 Live Attendees"}
                {planKey === "200" && "200 Live + 1,000 Automated Attendees"}
                {planKey === "500" && "500 Live + 2,500 Automated Attendees"}
              </Typography>
            )}
            {categoryTab === 1 && (
              <Typography
                variant="body2"
                color="#000"
                sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}
              >
                {planKey === "3000" && "3,000 Automated Attendees"}
                {planKey === "6000" && "6,000 Automated Attendees"}
                {planKey === "10000" && "10,000 Automated Attendees"}
              </Typography>
            )}
            {isYearly && (
              <Typography
                component="span"
                fontSize={20}
                color="rgba(0, 0, 0, 0.314)"
                fontWeight={500}
                mr={1}
                sx={{ textDecoration: "line-through" }}
              >
                ${plan.monthly}
              </Typography>
            )}
            <Typography
              variant="h5"
              fontWeight={700}
              color="#222"
              display="flex"
              alignItems="baseline"
              mb={0.5}
            >
              <span style={{ display: "flex", alignItems: "baseline", fontSize: "2rem" }}>
                $
                <Typography component="span" fontWeight="inherit" fontSize={34}>
                  {getDisplayPrice(plan)}
                </Typography>
              </span>
              <Typography component="span" fontSize={16} color="#666666" fontWeight={400} ml={1}>
                /month
              </Typography>
            </Typography>
            {isYearly && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Billed Annually
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={() => selectPlan(plan, isYearly)}
              sx={{
                width: "100%",
                mb: 1,
                bgcolor: "#2B2B30",
                color: "#fff",
                "&:hover": { bgcolor: "#1a1a1a" },
              }}
            >
              Buy now
            </Button>
            <Box sx={{ mt: 2 }}>
              {(planFeaturesList(isYearly) as Record<string, any[]>)[planKey]?.map(
                (section: any, sectionIndex: number) => (
                  <Box key={sectionIndex} sx={{ mb: sectionIndex === 0 ? 2 : 0 }}>
                    <Typography variant="body2" fontWeight={700} color="#222" mb={1.5}>
                      {section.title}
                    </Typography>
                    {section.list.map((feature: any, featureIndex: number) => (
                      <Box key={featureIndex} sx={{ mb: 0.85 }}>
                        <Typography
                          variant="body2"
                          color="#4a4a4a"
                          sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}
                        >
                          <Box sx={{ color: theme.palette.primary?.main, fontWeight: 600, fontSize: 16 }}>
                            ✓
                          </Box>
                          <span>{feature.label}</span>
                          {!!feature?.tooltip && (
                            <Tooltip arrow title={feature.tooltip || ""}>
                              <InfoIcon sx={{ fontSize: 14, color: "#999", cursor: "help" }} />
                            </Tooltip>
                          )}
                        </Typography>
                        {feature?.tag ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              mt: 0.25,
                              ml: 2.5,
                              px: 1,
                              py: 0.25,
                              bgcolor: "#d4edda",
                              color: "#155724",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 10,
                              textTransform: "lowercase",
                            }}
                          >
                            {feature.tag}
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                ),
              )}
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const FEATURES = useMemo(
    () => [
      { section: "Session Duration & Capacity" },
      { label: "2 Hour Session Duration (Live)", plans: [true, false, false] },
      { label: "4 Hour Session Duration (Live)", plans: [false, true, false] },
      { label: "Unlimited Session Duration (Automated)", plans: [false, true, true] },
      { label: "1 Host", plans: [true, true, true] },
      { label: "3 Co-Hosts/Moderators", plans: [false, true, true] },
      { section: "Live & Automated" },
      { label: "Live Chat with Automated sessions", plans: [false, true, true] },
      { label: "Reporting & Analytics", plans: [true, true, true] },
      { label: "Custom landing pages + embed forms", plans: [true, true, true] },
      { label: "Automated Webinars", plans: [false, true, true] },
      { label: "Evergreen Webinars", plans: [false, true, true] },
    ],
    [],
  );

  return (
    <Box className="v3-pricing-plan-table" sx={{ mb: 4 }} id={id}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        {!hideTabs && (
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} centered>
            <Tab label="Plans" sx={{ textTransform: "capitalize" }} />
            <Tab label="Compare Plans" sx={{ textTransform: "capitalize" }} />
          </Tabs>
        )}
      </Box>

      {tabValue === 0 ? (
        <Box className="ew-plan-boxes-con" mt={2}>
          <Box
            sx={{
              display: "inline-flex",
              border: "1px solid #d1d5db",
              borderRadius: "9999px",
              p: "4px",
              mb: 4,
            }}
          >
            <Button
              onClick={() => setCategoryTab(0)}
              sx={{
                textTransform: "none",
                borderRadius: "9999px",
                bgcolor: categoryTab === 0 ? "#ede9f7" : "transparent",
              }}
            >
              Live + Automated
            </Button>
            <Button
              onClick={() => setCategoryTab(1)}
              sx={{
                textTransform: "none",
                borderRadius: "9999px",
                bgcolor: categoryTab === 1 ? "#ede9f7" : "transparent",
              }}
            >
              Automated Only
            </Button>
          </Box>
          <Grid container spacing={2} width="100%" pt={5} sx={{ "--Grid-parent-columns": 9 } as any}>
            {planCardsData.map(({ key: planKey, plan }, index) => (
              <PlanCard key={planKey} planKey={planKey} plan={plan} isMostPopular={index === 1} />
            ))}
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            background: "#fff",
            borderRadius: 3,
            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
          }}
        >
          <Box
            display="flex"
            width="100%"
            minWidth={900}
            border="1px solid #E8EAF0"
            borderRadius="12px"
            bgcolor="#ffffff"
          >
            <Box sx={{ minWidth: 240, p: 4 }}>
              <Typography variant="h5" fontWeight={700}>
                Compare plans
              </Typography>
            </Box>
            {[
              { name: "Launch", value: liveValue, setter: setLiveValue, options: LIVE_PRICES },
              { name: "Scale", value: automatedValue, setter: setAutomatedValue, options: AUTOMATED_PRICES },
              { name: "Professional", value: liveAutomatedValue, setter: setLiveAutomatedValue, options: LIVE_AUTO_PRICES },
            ].map((plan) => (
              <Box key={plan.name} sx={{ flex: 1, minWidth: 220, p: 3, textAlign: "center" }}>
                <Typography variant="h6" fontWeight={600} color={theme.palette.primary.main} mb={1}>
                  {plan.name}
                </Typography>
                <Select
                  sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                  size="small"
                  value={plan.value}
                  onChange={(e) => plan.setter(e.target.value)}
                >
                  {plan.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Box>

          <Box sx={{ width: "100%", minWidth: 900, background: "#fff", borderRadius: "0 0 12px 12px" }}>
            {(showAllFeatures ? FEATURES : FEATURES.slice(0, 9)).map((feature: any, i: number) =>
              feature.section ? (
                <Box key={feature.section} sx={{ px: 4, py: 1.5, color: theme.palette.primary.main, fontSize: 22 }}>
                  {feature.section}
                </Box>
              ) : (
                <Box
                  key={feature.label}
                  display="flex"
                  alignItems="center"
                  sx={{ borderTop: i === 0 ? "none" : "1px solid #E8EAF0", py: 1.25 }}
                >
                  <Box sx={{ minWidth: 240, pl: 4, color: "#2B2B30" }}>{feature.label}</Box>
                  {feature.plans.map((val: boolean, idx: number) => (
                    <Box key={idx} sx={{ flex: 1, minWidth: 220, textAlign: "center" }}>
                      {val ? <CheckIcon sx={{ color: "#333333", fontSize: 19 }} /> : <Typography fontWeight={700}>-</Typography>}
                    </Box>
                  ))}
                </Box>
              ),
            )}
            <Box
              sx={{
                background: "#F0F0F0",
                px: 3,
                py: 1,
                display: "flex",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowAllFeatures((prev) => !prev)}
            >
              {showAllFeatures ? "Show less" : "Compare plans"}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PricingSectionV2GHL;
